import { useEffect, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { AttachmentDropzone } from '../../../../components/chat/AttachmentDropzone'
import { draftsToPendingAttachments } from '../../../../hooks/useAttachmentUpload'
import { useSystemIssueAttachmentUpload } from '../../hooks/useSystemIssueAttachmentUpload'
import { useCreateIssue } from '../../hooks/useSystemIssues'
import { useMentionCandidates } from '../../hooks/useSystemIssueMentions'
import { useProcessSidebarInfo } from '../../../occurrences/hooks/useProcessList'
import { useReportCompany } from '../../hooks/useReportCompany'
import { CompanySelect } from '../shared/CompanySelect'
import { BackButton } from '@/components/ui/BackButton'
import { filesFromClipboard } from '@/utils/clipboard'
import { MentionableTextarea } from '../../../occurrences/components/chat/MentionableTextarea'
import { PRIORITY_LABELS } from '../shared/IssueRow'
import { ProcessLinkField } from './ProcessLinkField'
import { ReportExamples } from './ReportExamples'
import type { SystemIssue, SystemIssuePriority } from '../../types/system-issues'
import type { ProcessListItem } from '../../../occurrences/types/occurrences'

interface NewIssueFormProps {
  onCreated: (issue: SystemIssue) => void
  /**
   * Processo detectado pela página atual (widget abriu enquanto o usuário
   * estava em `/processo/:processId`) — mostra um banner perguntando se quer
   * vincular, nunca vincula sozinho.
   */
  relatedProcessId?: number | null
  /**
   * Valor inicial do checkbox "Vincular ao processo?" — usado quando o
   * usuário já marcou a intenção de vincular antes de chegar aqui (ex: no
   * modal pequeno de Ocorrências do processo, antes de abrir este
   * formulário completo em outra aba).
   */
  initialLinkProcess?: boolean
  /**
   * Vínculo obrigatório com `relatedProcessId`: usado pela aba HUB dentro de
   * Ocorrências, onde o problema é SEMPRE do processo em que está sendo
   * aberto. Troca o checkbox por um aviso fixo.
   */
  lockProcessLink?: boolean
  /** Mostra o "Voltar" no topo do formulário (tela cheia de Atendimento
   * HUB). A aba HUB do processo já tem o próprio "Cancelar". */
  onBack?: () => void
}

const FIELD_CLASS =
  'w-full rounded-[5px] border border-border-subtle bg-surface px-3 py-1.5 text-xs focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900/40'
const LABEL_CLASS = 'mb-1 block text-xs font-medium text-body/80'

export function NewIssueForm({
  onCreated,
  relatedProcessId,
  initialLinkProcess = false,
  lockProcessLink = false,
  onBack,
}: NewIssueFormProps) {
  const { hasRank, capabilities } = useAuth()
  // Mesma restrição do backend (auth/authz/priority-gate.ts#canUseHighOrUrgentPriority,
  // RANK.coordenador) — replicada aqui só pra já não oferecer no dropdown uma
  // opção que o servidor recusaria, evitando um 403 confuso pro usuário.
  const canEscalatePriority = hasRank('coordenador')
  const availablePriorities: SystemIssuePriority[] = canEscalatePriority
    ? ['urgent', 'high', 'medium', 'low']
    : ['medium', 'low']

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState<SystemIssuePriority>('medium')
  const [linkProcess, setLinkProcess] = useState(
    relatedProcessId ? lockProcessLink || initialLinkProcess : false,
  )
  // Escolha manual — só usada quando não há `relatedProcessId` detectado por
  // contexto (formulário aberto direto na página de Ocorrências).
  const [manualProcess, setManualProcess] = useState<ProcessListItem | null>(
    null,
  )
  // Processo vindo do contexto (página do processo / aba HUB): número para o
  // rótulo e empresa para o seletor seguir.
  const { data: contextProcess } = useProcessSidebarInfo(relatedProcessId ?? null)
  const processLabel = relatedProcessId
    ? (contextProcess?.process_number ?? `#${relatedProcessId}`)
    : null
  const report = useReportCompany(
    (relatedProcessId ? contextProcess?.company_id : manualProcess?.company_id) ??
      null,
  )
  // Colegas da empresa escolhida pro `@` da descrição — quem é mencionado é
  // marcado na issue pelo backend (mesmo parser de Ocorrências).
  const { data: mentionCandidates = [] } = useMentionCandidates(report.companyId)
  const createIssue = useCreateIssue()
  const {
    drafts,
    addFiles,
    removeDraft,
    reset: resetAttachments,
    isUploading,
    hasErrors,
  } = useSystemIssueAttachmentUpload()

  useEffect(() => {
    if (!relatedProcessId) {
      setLinkProcess(false)
      return
    }
    if (lockProcessLink) setLinkProcess(true)
  }, [relatedProcessId, lockProcessLink])

  const canSubmit =
    title.trim().length > 0 &&
    description.trim().length > 0 &&
    !isUploading &&
    !hasErrors &&
    !createIssue.isLoading

  const linkedProcessId = relatedProcessId ?? manualProcess?.id ?? null
  const canPickProcess = capabilities?.canAccessOccurrences === true

  // Print colado com Ctrl+V na descrição vira anexo — mesmo caminho do
  // clipe (filtro de imagem, limite de tamanho/quantidade em
  // `useAttachmentUpload`). Texto colado segue normal.
  const handleDescriptionPaste = (
    e: React.ClipboardEvent<HTMLTextAreaElement>,
  ) => {
    const files = filesFromClipboard(e.clipboardData)
    if (files.length === 0) return
    e.preventDefault()
    addFiles(files)
  }

  const handleSubmit = () => {
    if (!canSubmit) return
    createIssue.mutate(
      {
        title: title.trim(),
        description: description.trim(),
        priority,
        related_process_id:
          linkProcess && linkedProcessId ? linkedProcessId : undefined,
        attachments: draftsToPendingAttachments(drafts),
        company_id: report.companyId ?? undefined,
      },
      {
        onSuccess: ({ issue }) => {
          setTitle('')
          setDescription('')
          setPriority('medium')
          // No modo travado (aba HUB) o vínculo não é opcional — limpar para
          // `false` deixaria um segundo report do mesmo formulário sair sem
          // processo, já que o efeito de `lockProcessLink` não roda de novo.
          setLinkProcess(lockProcessLink && !!relatedProcessId)
          setManualProcess(null)
          resetAttachments()
          onCreated(issue)
        },
      },
    )
  }

  const processLinkField = (
    <ProcessLinkField
      relatedProcessId={relatedProcessId ?? null}
      processLabel={processLabel}
      locked={lockProcessLink}
      linkProcess={linkProcess}
      onLinkProcessChange={setLinkProcess}
      manualProcess={manualProcess}
      onManualProcessChange={setManualProcess}
      canPickProcess={canPickProcess}
    />
  )
  // Seletor manual cabe na linha do título; os banners de processo já
  // detectado (aba HUB / página do processo) são largos e ficam embaixo.
  const processFieldInline = !relatedProcessId && canPickProcess

  return (
    <div className="flex flex-col gap-3 p-4">
      {onBack && (
        <div>
          <BackButton onClick={onBack} />
        </div>
      )}

      <div className="flex flex-wrap items-start gap-3">
        {/* flex-basis em vez de `sm:w-*`: o form também vive no widget
            flutuante (28rem), onde o breakpoint da JANELA não diz nada
            sobre o espaço disponível. */}
        <div className="min-w-0 flex-[2_1_16rem]">
          <label htmlFor="new-issue-title" className={LABEL_CLASS}>
            Título
          </label>
          <input
            id="new-issue-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={255}
            placeholder="Resuma o problema em poucas palavras"
            className={FIELD_CLASS}
          />
        </div>
        {processFieldInline && (
          <div className="min-w-0 flex-[1_1_18rem]">{processLinkField}</div>
        )}
        {report.showSelector && (
          <div className="flex-[0_1_14rem]">
            <label htmlFor="new-issue-company" className={LABEL_CLASS}>
              Empresa
            </label>
            <CompanySelect
              id="new-issue-company"
              options={report.options}
              value={report.companyId}
              onChange={report.setCompanyId}
              className={FIELD_CLASS}
            />
          </div>
        )}
        <div className="flex-[0_0_9rem]">
          <label htmlFor="new-issue-priority" className={LABEL_CLASS}>
            Prioridade
          </label>
          <select
            id="new-issue-priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value as SystemIssuePriority)}
            className={FIELD_CLASS}
          >
            {availablePriorities.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABELS[p]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!processFieldInline && processLinkField}

      <div>
        <label htmlFor="new-issue-description" className={LABEL_CLASS}>
          Descrição
        </label>
        <MentionableTextarea
          id="new-issue-description"
          candidates={mentionCandidates}
          value={description}
          onChange={setDescription}
          onPaste={handleDescriptionPaste}
          rows={5}
          placeholder="O que aconteceu? O que você esperava que acontecesse? Use @ para marcar um colega da sua empresa e Ctrl+V para colar um print."
          className="w-full resize-none rounded-[5px] border border-border-subtle bg-surface px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900/40"
        />
      </div>

      <AttachmentDropzone
        drafts={drafts}
        onAddFiles={addFiles}
        onRemove={removeDraft}
        accept="image/*"
      />

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="mt-1 inline-flex items-center justify-center gap-1.5 rounded-[5px] bg-[#2563eb] px-3 py-2 text-sm font-medium text-white transition hover:bg-[#1d55d6] disabled:opacity-50"
      >
        {createIssue.isLoading ? 'Enviando…' : 'Enviar'}
      </button>

      <ReportExamples onPick={setTitle} />
    </div>
  )
}
